import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { toStreamlineSolveSettings } from "@repo/shared/streamline";

import {
  EXCLUDED_PLAYERS,
  INCLUDED_PLAYERS,
  INCLUDE_ACTIVE_SQUAD,
  INCLUDE_OBJECTIVE_PLAYERS,
  INCLUDE_SBC_PLAYERS,
  PRIORITIZE_DUPLICATES,
  PRIORITIZE_SBC_STORAGE,
  PRIORITIZE_TRANSFERLIST,
  UNTRADEABLE_ONLY,
} from "../../settings";
import { SearchedPlayer } from "../players/SearchedPlayer.type";
import { IRootState } from "../store";

type State = {
  prioritizeDuplicates: boolean;
  includeTransferlist: boolean;
  prioritizeSbcStorage: boolean;
  useUntradeablesOnly: boolean;
  excludeActiveSquad: boolean;
  excludeSbcPlayers: boolean;
  excludeObjectivePlayers: boolean;
  includedPlayers: SearchedPlayer[];
  excludedPlayers: SearchedPlayer[];
  minRating: null | number;
  maxRating: null | number;
};

export const initialState: State = {
  prioritizeDuplicates: false,
  includeTransferlist: false,
  prioritizeSbcStorage: false,
  useUntradeablesOnly: false,
  excludeActiveSquad: true,
  excludeSbcPlayers: false,
  excludeObjectivePlayers: false,
  includedPlayers: [],
  excludedPlayers: [],
  minRating: null,
  maxRating: null,
};

const STORED_AS_IS = {
  useUntradeablesOnly: UNTRADEABLE_ONLY,
  prioritizeDuplicates: PRIORITIZE_DUPLICATES,
  prioritizeSbcStorage: PRIORITIZE_SBC_STORAGE,
  includeTransferlist: PRIORITIZE_TRANSFERLIST,
} as const;

const STORED_NEGATED = {
  excludeSbcPlayers: INCLUDE_SBC_PLAYERS,
  excludeObjectivePlayers: INCLUDE_OBJECTIVE_PLAYERS,
  excludeActiveSquad: INCLUDE_ACTIVE_SQUAD,
} as const;

const asIsFields = Object.keys(STORED_AS_IS) as (keyof typeof STORED_AS_IS)[];
const negatedFields = Object.keys(
  STORED_NEGATED,
) as (keyof typeof STORED_NEGATED)[];

const readBool = (key: string, fallback: boolean) => {
  const stored = localStorage.getItem(key);
  return stored === null ? fallback : stored === "true";
};

const persistSolveSettings = (settings: SolveSettings) => {
  asIsFields.forEach((field) =>
    localStorage.setItem(STORED_AS_IS[field], String(settings[field])),
  );
  negatedFields.forEach((field) =>
    localStorage.setItem(STORED_NEGATED[field], String(!settings[field])),
  );
  localStorage.setItem(
    INCLUDED_PLAYERS,
    JSON.stringify(settings.includedPlayers),
  );
  localStorage.setItem(
    EXCLUDED_PLAYERS,
    JSON.stringify(settings.excludedPlayers),
  );
};

const sbcFiltersSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    removeIncludedPlayer(state, action: PayloadAction<number>) {
      const newIncludedPlayers = [...state.includedPlayers].filter(
        (p) => p.resourceId !== action.payload,
      );
      state.includedPlayers = newIncludedPlayers;
      localStorage.setItem(
        INCLUDED_PLAYERS,
        JSON.stringify(newIncludedPlayers),
      );
    },
    updateMinRating(state, action: PayloadAction<number | null>) {
      state.minRating = action.payload;
    },
    updateMaxRating(state, action: PayloadAction<number | null>) {
      state.maxRating = action.payload;
    },
    /*
      The Customize panel edits a draft and commits it in one go, so the
      whole solve-settings block lands together.
    */
    applySolveSettings(state, action: PayloadAction<SolveSettings>) {
      Object.assign(state, action.payload);
      persistSolveSettings(action.payload);
    },
    initSbcParams(state) {
      asIsFields.forEach((field) => {
        state[field] = readBool(STORED_AS_IS[field], initialState[field]);
      });
      negatedFields.forEach((field) => {
        state[field] = !readBool(STORED_NEGATED[field], !initialState[field]);
      });

      state.includedPlayers =
        JSON.parse(localStorage.getItem(INCLUDED_PLAYERS)) ||
        ([] as SearchedPlayer[]);
      state.excludedPlayers =
        JSON.parse(localStorage.getItem(EXCLUDED_PLAYERS)) ||
        ([] as SearchedPlayer[]);
    },

    clearSbcParams(state) {
      persistSolveSettings(initialState);

      Object.assign(state, initialState);
    },
  },
});
/** Everything the Customize panel edits. */
export type SolveSettings = State;

export const SOLVE_SETTINGS_KEYS = [
  "prioritizeDuplicates",
  "includeTransferlist",
  "prioritizeSbcStorage",
  "useUntradeablesOnly",
  "excludeActiveSquad",
  "excludeSbcPlayers",
  "excludeObjectivePlayers",
  "includedPlayers",
  "excludedPlayers",
  "minRating",
  "maxRating",
] as const satisfies readonly (keyof SolveSettings)[];

export const pickSolveSettings = (state: State): SolveSettings =>
  Object.fromEntries(
    SOLVE_SETTINGS_KEYS.map((key) => [key, state[key]]),
  ) as SolveSettings;

/*
  Anything in the Customize panel set away from its default. Compared against
  `initialState` rather than restating each default, so a new setting only has
  to join SOLVE_SETTINGS_KEYS.
*/
export const solveSettingsActiveSelector = (state: IRootState) => {
  const f = state.sbcFiltersReducer;
  return SOLVE_SETTINGS_KEYS.some((key) => {
    const value = f[key];
    return Array.isArray(value)
      ? value.length > 0
      : value !== initialState[key];
  });
};

export const filtersActiveSelector = solveSettingsActiveSelector;

export const sbcFiltersSelector = (state: IRootState) =>
  state.sbcFiltersReducer;

export const streamlineSolveSettingsSelector = createSelector(
  sbcFiltersSelector,
  toStreamlineSolveSettings,
);
export const {
  applySolveSettings,
  removeIncludedPlayer,
  clearSbcParams,
  initSbcParams,
  updateMinRating,
  updateMaxRating,
} = sbcFiltersSlice.actions;
export default sbcFiltersSlice.reducer;
